import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { useMemo, useRef } from 'react';
import { getToken } from '../../../services/user-auth';

export type CopilotChat = ReturnType<typeof useCopilotChat>;

/**
 * @param reportContext The report the user is currently viewing (a data-route key such as
 * "commitment" or "mrr"). Sent with every message so the backend can bias which report it
 * reads toward the on-screen report for ambiguous questions.
 */
export function useCopilotChat(reportContext?: string) {
  // Keep the latest report context in a ref so the memoized transport closure always reads
  // the current value without being rebuilt (which would reset the chat).
  const reportContextRef = useRef(reportContext);
  reportContextRef.current = reportContext;

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${import.meta.env.VITE_WEFT_API}/reports/chat/stream`,
        prepareSendMessagesRequest: ({ id, messages }) => ({
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
          body: {
            chatId: id,
            messages,
            reportContext: reportContextRef.current,
          },
        }),
      }),
    [],
  );

  return useChat({ transport, experimental_throttle: 50 });
}
